/**
 * Buyer-portal app shell.
 *
 * Routes split into two groups:
 *   - Public: Calculator (/) — no layout wrapper
 *   - Dashboard: all authenticated pages — wrapped in DashboardLayout
 *     (top bar with logo/logout, left sidebar with nav menu)
 */

import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Outlet, useLocation } from "react-router-dom";

import { RootErrorBoundary, RouteErrorBoundary, InstallPrompt, DialogProvider, CookieConsent, Footer, PrivacyPolicy, TermsOfService, ConveyancingFees } from "@bc/ui";
import { Toaster } from "sonner";

import "./App.css";
import { AuthProvider } from "./context/AuthContext";
import DashboardLayout from "./components/layout/DashboardLayout";

const Calculator = lazy(() => import("./pages/Calculator"));
const ConveyancerPortal = lazy(() => import("./pages/ConveyancerPortal"));
const InitialQuotations = lazy(() => import("./pages/InitialQuotations"));
const SellersChoice = lazy(() => import("./pages/SellersChoice"));
const PaymentDetails = lazy(() => import("./pages/PaymentDetails"));
const AppointmentConfirmed = lazy(() => import("./pages/AppointmentConfirmed"));
const MyQuotations = lazy(() => import("./pages/MyQuotations"));
const MyPayments = lazy(() => import("./pages/MyPayments"));
const Profile = lazy(() => import("./pages/Profile"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));


function LoadingScreen() {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-gray-600 animate-pulse">Loading...</div>
    </div>
  );
}


function withRouteBoundary(element) {
  return (
    <RouteErrorBoundary>
      <Suspense fallback={<LoadingScreen />}>{element}</Suspense>
    </RouteErrorBoundary>
  );
}


// Public pages scroll normally with the footer in document flow. Dashboard
// routes render the footer inside DashboardLayout's fixed viewport frame
// instead, so the document itself never scrolls there.
function PublicShell() {
  // Public pages scroll the document — reset it on navigation so every page
  // opens at the top. (Dashboard routes do the same for their scroll pane
  // inside DashboardLayout.)
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <>
      <Outlet />
      <Footer />
    </>
  );
}


// Mobile browsers aggressively restore scroll positions from history on
// back/forward navigation, which can override the per-route scroll resets.
// Take manual control so every page reliably opens at the top.
if ("scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}

function App() {
  return (
    <RootErrorBoundary>
      <div className="App">
        <BrowserRouter>
          <AuthProvider>
          <DialogProvider>
            <Routes>
              {/* Public — no layout; footer flows after the page */}
              <Route element={<PublicShell />}>
                <Route path="/conveyancer" element={withRouteBoundary(<ConveyancerPortal />)} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/conveyancing-fees" element={<ConveyancingFees />} />
                <Route path="/forgot-password" element={withRouteBoundary(<ForgotPassword />)} />
                <Route path="/reset-password" element={withRouteBoundary(<ResetPassword />)} />
                <Route path="/verify-email" element={withRouteBoundary(<VerifyEmail />)} />
              </Route>

              {/* Dashboard — wrapped in DashboardLayout (top bar + sidebar
                  + footer all pinned; only the content pane scrolls) */}
              <Route element={<DashboardLayout />}>
                <Route path="/" element={withRouteBoundary(<Calculator />)} />
                <Route path="/my-quotations" element={withRouteBoundary(<MyQuotations />)} />
                <Route path="/initial-quotations" element={withRouteBoundary(<InitialQuotations />)} />
                <Route path="/sellers-choice" element={withRouteBoundary(<SellersChoice />)} />
                <Route path="/payment-details" element={withRouteBoundary(<PaymentDetails />)} />
                <Route path="/my-payments" element={withRouteBoundary(<MyPayments />)} />
                <Route path="/appointment-confirmed" element={withRouteBoundary(<AppointmentConfirmed />)} />
                <Route path="/profile" element={withRouteBoundary(<Profile />)} />
              </Route>
            </Routes>
            <CookieConsent />
            <InstallPrompt />
          </DialogProvider>
          </AuthProvider>
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </div>
    </RootErrorBoundary>
  );
}

export default App;
